// Where the call shows: full screen over the app, the corner tile, its own pop-out window, the guest page. Pure
// layout: the call itself lives in <CallSession/> and never moves.
import { AppWindow, CircleDot, Maximize2, PhoneOff } from 'lucide-react';
import { EDGES } from './useFloatingWindow';

const NAVY = '#0D1B2A';

export function FullFrame({ header, children, wide = true, white = false }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-3 sm:p-6" data-testid="active-call-overlay">
      <div className={`my-auto w-full ${wide ? 'max-w-6xl' : 'max-w-4xl'} rounded-2xl ${white ? 'bg-white p-6 shadow-2xl sm:p-8' : 'space-y-3 p-4'}`}
        style={white ? undefined : { background: 'var(--strata-canvas)' }}>
        {header}
        {children}
      </div>
    </div>
  );
}

// The in-app call window: floats over the app (no backdrop), moved by its title bar, resized from any edge or
// corner. `floating` false = fills the page (guest link).
export function CallWindow({ win, floating = true, children }) {
  const style = floating ? { left: win.rect.x, top: win.rect.y, width: win.rect.w, height: win.rect.h } : undefined;
  return (
    <div className={`fixed z-[60] flex flex-col overflow-hidden bg-[#0A1521] ${floating ? 'rounded-xl border border-white/10 shadow-[0_24px_80px_rgba(0,0,0,0.55)]' : 'inset-0'} ${win?.moving ? 'select-none' : ''}`}
      style={style} data-testid="active-call-overlay">
      {children}
      {floating && Object.entries(EDGES).map(([edge, e]) => (
        <div key={edge} className={`absolute z-40 ${e.cls}`} style={{ cursor: e.cursor, touchAction: 'none' }} {...win.edgeProps(edge)} data-testid={`mm-resize-${edge}`} />
      ))}
      {floating && (
        <svg className="pointer-events-none absolute bottom-1 right-1 z-40 h-3 w-3 text-white/35" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M11 4L4 11M11 8L8 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      )}
    </div>
  );
}

export function MiniFrame({ title, onExpand, children }) {
  return (
    <div className="fixed bottom-4 right-4 z-[60] w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border shadow-2xl"
      style={{ borderColor: 'rgba(255,255,255,0.14)', background: NAVY }} data-testid="active-call-pip">
      <div className="absolute left-0 right-0 top-0 z-30 flex items-center justify-between gap-2 bg-gradient-to-b from-black/70 to-transparent px-2.5 py-1.5">
        <span className="flex min-w-0 items-center gap-1.5 text-[12px] font-semibold text-white">
          <CircleDot className="h-3.5 w-3.5 shrink-0 animate-pulse text-red-500" /><span className="truncate">{title}</span>
        </span>
        <button type="button" onClick={onExpand} title="Expand meeting" className="rounded-md p-1 text-white/90 hover:bg-white/15" data-testid="active-call-expand">
          <Maximize2 className="h-4 w-4" />
        </button>
      </div>
      <div className="h-[210px]">{children}</div>
    </div>
  );
}

export function PoppedPill({ title, onBack, onLeave }) {
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 rounded-full border border-white/15 py-1.5 pl-3 pr-1.5 text-xs text-white shadow-2xl"
      style={{ background: NAVY }} data-testid="active-call-popped">
      <CircleDot className="h-3.5 w-3.5 shrink-0 animate-pulse text-red-500" />
      <span className="max-w-[220px] truncate"><span className="font-semibold">{title}</span> is in its own window</span>
      <button type="button" onClick={onBack} className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 font-semibold transition-colors duration-150 hover:bg-white/25"
        data-testid="active-call-popin">
        <AppWindow className="h-3.5 w-3.5" /> Bring back
      </button>
      <button type="button" onClick={onLeave} title="Leave" aria-label="Leave" className="rounded-full bg-red-600 p-1.5 transition-colors duration-150 hover:bg-red-700"
        data-testid="active-call-popped-leave">
        <PhoneOff className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function GuestFrame({ title, card, children }) {
  return (
    <div className="min-h-screen w-full p-3 sm:p-6" style={{ background: NAVY }}>
      <div className="mx-auto max-w-5xl">
        <div className="mb-3 text-sm font-semibold" style={{ color: '#E8EEF4' }}>{title} · you joined as a guest</div>
        {card ? <div className="rounded-2xl bg-white p-6 shadow-2xl sm:p-8">{children}</div> : children}
      </div>
    </div>
  );
}
